import { useEffect, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { X } from 'lucide-react'
import { Button } from './Button'

/**
 * Reusable modal for a two-choice decision (confirm / cancel). Extracted from
 * the Start Over confirmation so every in-app dialog shares the same overlay,
 * scroll-lock, Escape-to-dismiss, and motion.
 *
 * `tone` picks the emphasis: 'danger' for destructive confirms (red icon tile +
 * danger confirm button), 'neutral' for an ordinary either/or choice (brand
 * icon tile + primary confirm button). Dismissing — Escape, backdrop click, or
 * the cancel button — always calls `onCancel`, so the safe outcome should live
 * there.
 */
interface ConfirmDialogProps {
  open: boolean
  title: string
  description: ReactNode
  confirmLabel: string
  /** Footer cancel button. Omit to drop it — pair with `showClose` so a
   *  top-right X carries the dismiss instead of a labelled footer button. */
  cancelLabel?: string
  onConfirm: () => void
  onCancel: () => void
  tone?: 'neutral' | 'danger'
  /** Leading glyph in the icon tile (e.g. a lucide icon). */
  icon?: ReactNode
  /** Show a top-right close (X) button that calls onCancel. */
  showClose?: boolean
  /** Optional third choice, rendered as a destructive text button between
   *  cancel and confirm — for a two-action decision (e.g. delete-all vs
   *  keep-some) where dismissing still means "do nothing". */
  secondaryLabel?: string
  onSecondary?: () => void
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  tone = 'neutral',
  icon,
  showClose = false,
  secondaryLabel,
  onSecondary,
}: ConfirmDialogProps) {
  const shouldReduceMotion = useReducedMotion()

  // While the dialog is open: lock background scroll and let Escape cancel.
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onCancel])

  const dur = shouldReduceMotion ? 0 : 0.3
  const tileBg = tone === 'danger' ? 'var(--color-error-surface)' : 'var(--color-ai-badge-surface)'

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: dur }}
          onClick={onCancel}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60, // --z-overlay
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-6)',
            background: 'var(--color-scrim-dialog)',
            backdropFilter: 'var(--glass-subtle-blur)',
            WebkitBackdropFilter: 'var(--glass-subtle-blur)',
          }}
        >
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-desc"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12, scale: shouldReduceMotion ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: shouldReduceMotion ? 0 : 8, scale: shouldReduceMotion ? 1 : 0.98 }}
            transition={{ duration: dur, ease: [0.0, 0.0, 0.2, 1.0] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 420,
              background: 'var(--color-surface-container-lowest)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-ambient)',
              padding: 'var(--spacing-8)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {showClose && (
              <button
                type="button"
                aria-label="Close"
                onClick={onCancel}
                style={{
                  position: 'absolute',
                  top: 'var(--spacing-6)',
                  right: 'var(--spacing-6)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  border: 'none',
                  borderRadius: 'var(--radius-full)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                }}
              >
                <X size={20} />
              </button>
            )}
            {icon && (
              <div
                aria-hidden="true"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 'var(--radius-lg)',
                  background: tileBg,
                  color: tone === 'danger' ? 'var(--color-error-text)' : 'var(--color-action-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 'var(--spacing-6)',
                }}
              >
                {icon}
              </div>
            )}

            <h2
              id="confirm-dialog-title"
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 'var(--weight-semibold)',
                fontSize: 'var(--text-title-lg)',
                color: 'var(--color-text-primary)',
                lineHeight: 'var(--leading-tight)',
                margin: '0 0 var(--spacing-3) 0',
              }}
            >
              {title}
            </h2>
            <p
              id="confirm-dialog-desc"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-md)',
                color: 'var(--color-text-secondary)',
                lineHeight: 'var(--leading-normal)',
                margin: 0,
              }}
            >
              {description}
            </p>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 'var(--spacing-3)',
                marginTop: 'var(--spacing-8)',
              }}
            >
              {cancelLabel && (
                <Button variant="tertiary" type="button" onClick={onCancel}>
                  {cancelLabel}
                </Button>
              )}
              {secondaryLabel && onSecondary && (
                <Button variant="danger-text" type="button" onClick={onSecondary}>
                  {secondaryLabel}
                </Button>
              )}
              <Button variant={tone === 'danger' ? 'danger' : 'primary'} type="button" onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
